import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Row, Col, Card, CardBody, Table, Spinner, Alert, Input, Button,
  FormGroup, Label, Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import axiosInstance from "../../Request";
import { REPORTING_URL, URL } from "../../api";

// Report catalogue -> standalone SaloraReporting endpoints (:8081).
// "range" reports use fromDate/toDate; "ason" uses a single as-on date.
const REPORTS = [
  {
    key: "loan", label: "Loan Report", url: REPORTING_URL.MERCHANT_LOAN, dated: "range",
    icon: "ni ni-money-coins",
    desc: "All loans in the selected period. Includes loan code, borrower, amount, ROI/day & /annum, EMI, APR, fees, GST, UTR and status.",
  },
  {
    key: "disbursement", label: "Disbursement Report", url: REPORTING_URL.MERCHANT_DISBURSEMENT, dated: "range",
    icon: "ni ni-send",
    desc: "Net amount disbursed per loan in the period, with processing fee, GST, UTR and the bank account / UPI the payout reached.",
  },
  {
    key: "collection", label: "Collection Report", url: REPORTING_URL.MERCHANT_COLLECTION, dated: "range",
    icon: "ni ni-chart-bar-32",
    desc: "Money collected per payment date, split into principal, interest and penal, for the selected period.",
  },
  {
    key: "outstanding", label: "Outstanding (As-On)", url: REPORTING_URL.MERCHANT_OUTSTANDING, dated: "ason",
    icon: "ni ni-watch-time",
    desc: "Outstanding principal, interest, penal and DPD as on a chosen date — adds back any collections made after that date.",
  },
  {
    key: "customer", label: "Customer Data", url: REPORTING_URL.MERCHANT_CUSTOMER, dated: "range",
    icon: "ni ni-single-02",
    desc: "Customer master — identity, DOB/gender/marital, PAN/Aadhaar/CKYC, current/office/permanent address, company & annual income, employment, father name and loan count. Date window filters by registration date; also filter by fintech and Customer ID/Code.",
  },
  {
    key: "emiSchedule", label: "EMI Schedule", url: REPORTING_URL.MERCHANT_EMI_SCHEDULE, dated: "range",
    icon: "ni ni-bullet-list-67",
    desc: "Every EMI across BOTH schemes (FIP + PIF, shown in a 'scheme' column): EMI date/number/amount, principal & interest due, outstanding principal/interest, pending amount, DPD and EMI status. Date window filters by EMI date.",
  },
  {
    key: "income", label: "Income Report", url: REPORTING_URL.MERCHANT_INCOME, dated: "range",
    icon: "ni ni-chart-pie-35",
    desc: "Income per loan by type — Processing (fee booked at disbursal, with GST) and Interest (interest collected per payment date, both schemes). Date window filters by the income date.",
  },
  {
    key: "charge", label: "Charge Report", url: REPORTING_URL.MERCHANT_CHARGE, dated: "range",
    icon: "ni ni-bell-55",
    desc: "Charges levied on loans — penal, bounce, overdue and late-penalty (type shown per row; GST applies to bounce only). Date window filters by the charge date.",
  },
  {
    key: "waiver", label: "Waiver Report", url: REPORTING_URL.MERCHANT_WAIVER, dated: "range",
    icon: "ni ni-scissors",
    desc: "Amounts written off per loan — principal, interest, penalty or bounce charge (type shown per row). Date window filters by the waiver date.",
  },
];

// filterType -> backend param name (1:1). Each matches either the numeric id or the code.
const FILTER_LABELS = {
  loanCode: "Loan Code (LI…)",
  customerId: "Customer ID / Code",
  leadId: "Lead ID / Code",
  applicationId: "Application ID",
};

const FORMATS = [
  { value: "xlsx", label: "Excel (.xlsx)" },
  { value: "csv", label: "CSV (.csv)" },
  { value: "txt", label: "Text (.txt)" },
];

const MONEY_KEYS = new Set([
  "loan_amount", "disbursed_amount", "net_disbursed", "emi_amount", "processing_fee",
  "processing_fees", "gst", "principal", "interest", "penal", "total",
  "os_principal", "os_interest", "os_penal",
]);

const prettify = (k) =>
  k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
   .replace(/Roi/g, "ROI").replace(/Apr/g, "APR").replace(/Gst/g, "GST")
   .replace(/Utr/g, "UTR").replace(/Upi/g, "UPI").replace(/Dpd/g, "DPD")
   .replace(/Pan/g, "PAN").replace(/Os /g, "O/S ").replace(/Emi/g, "EMI");

// preview: money -> ₹ + 2dp; any other decimal value -> exactly 2 digits (344.45); integers/ids untouched
const fmtCell = (key, val) => {
  if (val === null || val === undefined || val === "") return "-";
  const n = Number(val);
  const numeric = String(val).trim() !== "" && !isNaN(n) && Number.isFinite(n);
  if (numeric) {
    const r = Math.round(n * 100) / 100;
    if (MONEY_KEYS.has(key)) {
      return "₹" + r.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (/^-?\d+\.\d+$/.test(String(val))) return r.toFixed(2); // decimal -> 2 digits
  }
  return String(val);
};

// export: round any decimal-looking value to 2 digits as a real number; leave ids/codes/integers as-is
const fmtExport = (val) => {
  if (val === null || val === undefined) return "";
  if (typeof val === "number") return Number.isInteger(val) ? val : Math.round(val * 100) / 100;
  if (/^-?\d+\.\d+$/.test(String(val))) return Math.round(Number(val) * 100) / 100;
  return val;
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const MerchantReports = () => {
  const [fintechList, setFintechList] = useState([]);

  // modal + filters
  const [modalOpen, setModalOpen] = useState(false);
  const [activeKey, setActiveKey] = useState("loan");
  const [filename, setFilename] = useState("");
  const [format, setFormat] = useState("xlsx");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [asOnDate, setAsOnDate] = useState(todayIso());
  const [filterType, setFilterType] = useState("loanCode");
  const [filterValue, setFilterValue] = useState("");
  const [fintechId, setFintechId] = useState("");
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState("");

  // inline preview
  const [preview, setPreview] = useState(null); // { key, rows }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const activeReport = useMemo(() => REPORTS.find((r) => r.key === activeKey), [activeKey]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get(URL.LSP_LIST);
        setFintechList((res.data || []).filter((f) => f.isActive));
      } catch (e) {
        console.error("Failed to load fintech list", e);
      }
    })();
  }, []);

  const openModal = (key) => {
    setActiveKey(key);
    setFilename("");
    setModalError("");
    setModalOpen(true);
  };

  const buildParams = (report) => {
    const params = {};
    if (report.dated === "range") {
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
    } else {
      params.asOnDate = asOnDate;
    }
    if (fintechId) params.fintechId = fintechId;
    if (filterValue && filterValue.trim()) params[filterType] = filterValue.trim();
    return params;
  };

  const fetchRows = async (report) => {
    const res = await axiosInstance.get(report.url, { params: buildParams(report) });
    return Array.isArray(res.data) ? res.data : [];
  };

  const exportRows = (rows, report) => {
    const name = (filename && filename.trim())
      ? filename.trim()
      : `${report.key}_report_${report.dated === "ason" ? asOnDate : (toDate || todayIso())}`;
    const cols = Object.keys(rows[0]);
    const aoa = [cols.map(prettify), ...rows.map((r) => cols.map((c) => fmtExport(r[c])))];

    if (format === "xlsx") {
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Report");
      XLSX.writeFile(wb, `${name}.xlsx`);
    } else if (format === "csv") {
      const csv = XLSX.utils.sheet_to_csv(XLSX.utils.aoa_to_sheet(aoa));
      saveAs(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }), `${name}.csv`);
    } else {
      // tab-separated plain text
      const txt = aoa.map((row) => row.map((c) => String(c ?? "")).join("\t")).join("\r\n");
      saveAs(new Blob([txt], { type: "text/plain;charset=utf-8;" }), `${name}.txt`);
    }
  };

  const handleDownload = async () => {
    if (activeReport.dated === "ason" && !asOnDate) {
      setModalError("As-On date is required.");
      return;
    }
    setBusy(true);
    setModalError("");
    try {
      const rows = await fetchRows(activeReport);
      if (!rows.length) {
        setModalError("No data for the selected filters — nothing to download.");
        return;
      }
      exportRows(rows, activeReport);
      setModalOpen(false);
    } catch (e) {
      setModalError(
        e?.response?.status === 401
          ? "Session expired or not authorized. Please log in again."
          : "Failed to generate the report. Is the reporting service reachable?"
      );
    } finally {
      setBusy(false);
    }
  };

  const handlePreview = async () => {
    if (activeReport.dated === "ason" && !asOnDate) {
      setModalError("As-On date is required.");
      return;
    }
    setBusy(true);
    setModalError("");
    setPreviewError("");
    setPreviewLoading(true);
    try {
      const rows = await fetchRows(activeReport);
      setPreview({ key: activeReport.key, label: activeReport.label, rows });
      setModalOpen(false);
    } catch (e) {
      setModalError(
        e?.response?.status === 401
          ? "Session expired or not authorized. Please log in again."
          : "Failed to load the preview. Is the reporting service reachable?"
      );
    } finally {
      setBusy(false);
      setPreviewLoading(false);
    }
  };

  // Preview directly from a card (uses current filters, which default to blank = all).
  const handlePreviewFromCard = (report) => {
    setActiveKey(report.key);
    setPreviewError("");
    setPreviewLoading(true);
    (async () => {
      try {
        const rows = await fetchRows(report);
        setPreview({ key: report.key, label: report.label, rows });
      } catch (e) {
        setPreviewError(
          e?.response?.status === 401
            ? "Session expired or not authorized. Please log in again."
            : "Failed to load the preview. Is the reporting service reachable?"
        );
      } finally {
        setPreviewLoading(false);
      }
    })();
  };

  const cardStyle = {
    border: "1px solid #e9ecef", borderRadius: "10px", height: "100%",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  };
  const previewCols = preview?.rows?.length ? Object.keys(preview.rows[0]) : [];

  return (
    <>
      <div className="header bg-gradient-info pb-6 pt-4 pt-md-7 mb-4">
        <Container fluid>
          <Row className="align-items-center">
            <Col>
              <h1 className="text-white mb-0">Reports</h1>
              <p className="text-white mb-0" style={{ opacity: 0.85 }}>
                Generate &amp; download loan reports — loan, disbursement, collection &amp; outstanding.
              </p>
            </Col>
            <Col xs="auto">
              <Button color="white" onClick={() => openModal(activeKey)}>
                <i className="ni ni-cloud-download-95 mr-1" /> Download report
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      <Container fluid className="mt--6">
        <h6 className="text-uppercase text-muted mb-3">Loans</h6>
        <Row>
          {REPORTS.map((r) => (
            <Col lg="6" xl="4" key={r.key} className="mb-4">
              <Card style={cardStyle}>
                <CardBody>
                  <div className="d-flex align-items-center mb-2">
                    <span
                      className="icon icon-shape bg-gradient-info text-white rounded-circle mr-3"
                      style={{ width: 44, height: 44 }}
                    >
                      <i className={r.icon} />
                    </span>
                    <h4 className="mb-0">{r.label}</h4>
                  </div>
                  <p style={{ fontSize: "13px", minHeight: "66px", color: "#525f7f" }}>
                    {r.desc}
                  </p>
                  <div className="d-flex" style={{ gap: "16px" }}>
                    <Button color="link" className="p-0" onClick={() => openModal(r.key)}>
                      <i className="ni ni-cloud-download-95 mr-1" /> Download Report
                    </Button>
                    <Button
                      color="link" className="p-0 text-muted"
                      onClick={() => { setActiveKey(r.key); handlePreviewFromCard(r); }}
                    >
                      Preview
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>

        {/* inline preview */}
        {previewLoading ? (
          <div className="text-center p-4"><Spinner size="sm" /> Loading preview…</div>
        ) : previewError ? (
          <Alert color="danger">{previewError}</Alert>
        ) : preview ? (
          <Card style={{ ...cardStyle, marginBottom: "2rem" }}>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h4 className="mb-0">{preview.label} — preview</h4>
                <span className="text-muted" style={{ fontSize: "12px" }}>{preview.rows.length} row(s)</span>
              </div>
              <div style={{ overflowX: "auto", maxHeight: 480 }}>
                <Table bordered striped hover responsive size="sm" className="text-nowrap" style={{ fontSize: "11px" }}>
                  <thead className="thead-light">
                    <tr>
                      <th>#</th>
                      {previewCols.map((c) => <th key={c}>{prettify(c)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.length ? (
                      preview.rows.map((row, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          {previewCols.map((c) => <td key={c}>{fmtCell(c, row[c])}</td>)}
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan={previewCols.length + 1} className="text-center">No rows for these filters.</td></tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        ) : null}
      </Container>

      {/* Download modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg" centered>
        <ModalHeader toggle={() => setModalOpen(false)}>
          Download report for your business
        </ModalHeader>
        <ModalBody>
          <p className="text-muted" style={{ fontSize: "13px" }}>
            Select the report, period and format. Reports are generated live from the loan book.
          </p>

          <FormGroup>
            <Label className="fw-bold">Report</Label>
            <Input type="select" value={activeKey} onChange={(e) => setActiveKey(e.target.value)}>
              {REPORTS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </Input>
          </FormGroup>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label className="fw-bold">Save Report As <span className="text-muted">(optional)</span></Label>
                <Input type="text" placeholder="Eg: Loan Report — Oct" value={filename}
                  onChange={(e) => setFilename(e.target.value)} />
                <small className="text-muted">File name for your report.</small>
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label className="fw-bold">Select Format</Label>
                <Input type="select" value={format} onChange={(e) => setFormat(e.target.value)}>
                  {FORMATS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </Input>
                <small className="text-muted">Excel, CSV or plain text.</small>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            {activeReport?.dated === "range" ? (
              <>
                <Col md="6">
                  <FormGroup>
                    <Label className="fw-bold">From Date</Label>
                    <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label className="fw-bold">To Date</Label>
                    <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                  </FormGroup>
                </Col>
              </>
            ) : (
              <Col md="6">
                <FormGroup>
                  <Label className="fw-bold">As On Date</Label>
                  <Input type="date" value={asOnDate} onChange={(e) => setAsOnDate(e.target.value)} />
                </FormGroup>
              </Col>
            )}
          </Row>

          <Row>
            <Col md="4">
              <FormGroup>
                <Label className="fw-bold">Search By</Label>
                <Input type="select" value={filterType}
                  onChange={(e) => { setFilterType(e.target.value); setFilterValue(""); }}>
                  {Object.entries(FILTER_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </Input>
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="fw-bold">{FILTER_LABELS[filterType]}</Label>
                <Input type="text" value={filterValue} placeholder={FILTER_LABELS[filterType]}
                  onChange={(e) => setFilterValue(e.target.value)} />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="fw-bold">Fintech</Label>
                <Input type="select" value={fintechId} onChange={(e) => setFintechId(e.target.value)}>
                  <option value="">All Fintechs</option>
                  {fintechList.map((f) => <option key={f.id} value={f.id}>{f.brandName || f.name}</option>)}
                </Input>
              </FormGroup>
            </Col>
          </Row>

          {modalError ? <Alert color="danger" className="mb-0">{modalError}</Alert> : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={handlePreview} disabled={busy}>
            {busy ? <Spinner size="sm" /> : "Preview"}
          </Button>
          <Button color="primary" onClick={handleDownload} disabled={busy}>
            {busy ? <><Spinner size="sm" className="mr-1" /> Working…</> : <><i className="ni ni-cloud-download-95 mr-1" /> Download Report</>}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default MerchantReports;
